import React, { useState } from "react"
import { Box, Button, useTheme } from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { useTranslation } from "react-i18next"

import { SendButton } from "./SendButton"
import {
  acceptableFileFormats,
  acceptableFileTypes,
  acceptableZipFileTypes,
  FileDropzoneArea,
  FilesItem
} from "./droparea/FileDropzone"
import { ReferenceFile, Comment } from "src/services/api/document"
import { UploadedJobFile } from "./droparea/expiringFiles"

interface TranslateFileDropzoneProps {
  files: FilesItem[]
  setFiles: React.Dispatch<React.SetStateAction<FilesItem[]>>
  langFrom: string
  isMobile: boolean
  showInstructionsButton: boolean
  setInstructions: (open: boolean) => void
  referenceFiles: ReferenceFile[]
  comments: Comment[]
  uploadFiles: (file: File) => Promise<UploadedJobFile | UploadedJobFile[]>
  deleteFile: (fileId: string) => Promise<void>
  anyFileIncorrect: boolean
  noValidFiles: boolean
  noCommunity: boolean
  noTargetLanguages: boolean
  noSourceLanguage: boolean
  quoting: boolean
  sendButtonTooltip: string
  onSend: () => void
}

export const TranslateFileDropzone: React.FC<TranslateFileDropzoneProps> = ({
  files,
  setFiles,
  langFrom,
  isMobile,
  showInstructionsButton,
  setInstructions,
  referenceFiles,
  comments,
  uploadFiles,
  deleteFile,
  anyFileIncorrect,
  noValidFiles,
  noCommunity,
  noTargetLanguages,
  noSourceLanguage,
  quoting,
  sendButtonTooltip,
  onSend
}) => {
  const [isDragOver, setDragOver] = useState<boolean>(false)
  const { palette } = useTheme()
  const { t } = useTranslation("common")

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "25px",
        height: { xs: "auto", md: "100%" },
        flex: 1,
        "&.MuiContainer-root": {
          pl: { xs: 2, md: 10 },
          pr: { xs: 2, md: 10 }
        }
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          border: "1px dashed #BABABA",
          borderBottom: "none",
          borderRadius: "inherit",
          borderBottomLeftRadius: "0",
          borderBottomRightRadius: "0",
          background: "#FAFAFA 0% 0% no-repeat padding-box",
          ...(isDragOver ? { border: `1px dashed ${palette.primary.main}`, backgroundColor: palette.primary.light } : {})
        }}
      >
        <FileDropzoneArea
          files={files}
          setFiles={setFiles}
          isDragOver={isDragOver}
          setDragOver={setDragOver}
          id={"job-files-dropzone"}
          fileFormats={acceptableFileFormats}
          fileTypes={[...acceptableFileTypes, ...acceptableZipFileTypes]}
          placing="Portal Job input files"
          languageFrom={langFrom}
          uploadFiles={uploadFiles}
          showRemoveAll
          deleteFile={deleteFile}
          fileFormatsDescription={isMobile ? ".doc, .docx, .html, .odp, .odt, .pdf, .ppt, .pptx, .xls, .xlsx" : undefined}
        />
      </Box>
      {
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            flexGrow: 0,
            borderBottomLeftRadius: "25px",
            borderBottomRightRadius: "25px",
            overflow: "hidden" /* ensures child respects rounded corners */
          }}
        >
          {showInstructionsButton && (
            <Button
              onClick={() => setInstructions(true)}
              variant="outlined"
              sx={{ flex: 1, borderRadius: 0, borderBottomLeftRadius: "inherit" }}
              disabled={!files.length}
              fullWidth
              endIcon={(!!referenceFiles.length || !!comments.length) && <CheckCircleIcon color="primary" />}
            >
              {t("document.instructions")}
            </Button>
          )}
          <Box
            sx={{
              flex: 1,
              borderRadius: 0,
              borderBottomLeftRadius: showInstructionsButton ? "0" : "inherit",
              borderBottomRightRadius: "inherit"
            }}
          >
            <SendButton
              anyFileIncorrect={anyFileIncorrect}
              noValidFiles={noValidFiles}
              noCommunity={noCommunity}
              noTargetLanguages={noTargetLanguages}
              noSourceLanguage={noSourceLanguage}
              quoting={quoting}
              sendButtonTooltip={sendButtonTooltip}
              onSend={onSend}
            />
          </Box>
        </Box>
      }
    </Box>
  )
}

export default TranslateFileDropzone


